export enum PollyEngine {
  STANDARD = 'standard',
  NEURAL = 'neural',
  LONG_FORM = 'long-form',
  GENERATIVE = 'generative',
}

export enum PollyVoice {
  JOANNA = 'Joanna',
}

export enum PollyLanguage {
  ARABIC = 'arb',
  CHINESE = 'cn',
  CZECH = 'cz',
  DANISH = 'dk',
  DUTCH = 'nl',
  ENGLISH = 'en',
  FINNISH = 'fi',
  FRENCH = 'fr',
  GERMAN = 'de',
  HINDI = 'in',
  ICELANDIC = 'is',
  ITALIAN = 'it',
  JAPANESE = 'jp',
  KOREAN = 'kr',
  NORWEGIAN = 'no',
  POLISH = 'pl',
  PORTUGUESE = 'pt',
  ROMANIAN = 'ro',
  RUSSIAN = 'ru',
  SPANISH = 'es',
  SWEDISH = 'se',
  TURKISH = 'tr',
  WELSH = 'gb',
}

export const DEFAULT_SCRIPT_LANGUAGES: PollyLanguage[] = [
  PollyLanguage.ENGLISH,
  PollyLanguage.FRENCH,
  PollyLanguage.PORTUGUESE,
  PollyLanguage.SPANISH,
];

// Best available engine per language: generative > neural > standard.
// Female voices only.
export const voicesByLanguage: Record<
  PollyLanguage,
  { engine: PollyEngine; voices: string[] }
> = {
  [PollyLanguage.ARABIC]: { engine: PollyEngine.NEURAL, voices: ['Hala'] },
  [PollyLanguage.CHINESE]: { engine: PollyEngine.NEURAL, voices: ['Zhiyu'] },
  [PollyLanguage.CZECH]: { engine: PollyEngine.NEURAL, voices: ['Jitka'] },
  [PollyLanguage.DANISH]: { engine: PollyEngine.NEURAL, voices: ['Sofie'] },
  [PollyLanguage.DUTCH]: { engine: PollyEngine.NEURAL, voices: ['Laura'] },
  [PollyLanguage.ENGLISH]: {
    engine: PollyEngine.GENERATIVE,
    voices: ['Amy', 'Danielle', 'Joanna', 'Niamh', 'Ruth', 'Salli'],
  },
  [PollyLanguage.FINNISH]: { engine: PollyEngine.NEURAL, voices: ['Suvi'] },
  [PollyLanguage.FRENCH]: { engine: PollyEngine.NEURAL, voices: ['Lea'] },
  [PollyLanguage.GERMAN]: { engine: PollyEngine.NEURAL, voices: ['Vicki'] },
  [PollyLanguage.HINDI]: { engine: PollyEngine.NEURAL, voices: ['Kajal'] },
  [PollyLanguage.ICELANDIC]: { engine: PollyEngine.STANDARD, voices: ['Dora'] },
  [PollyLanguage.ITALIAN]: { engine: PollyEngine.NEURAL, voices: ['Bianca'] },
  [PollyLanguage.JAPANESE]: {
    engine: PollyEngine.NEURAL,
    voices: ['Kazuha', 'Tomoko'],
  },
  [PollyLanguage.KOREAN]: { engine: PollyEngine.NEURAL, voices: ['Seoyeon'] },
  [PollyLanguage.NORWEGIAN]: { engine: PollyEngine.NEURAL, voices: ['Ida'] },
  [PollyLanguage.POLISH]: { engine: PollyEngine.NEURAL, voices: ['Ola'] },
  [PollyLanguage.PORTUGUESE]: {
    engine: PollyEngine.GENERATIVE,
    voices: ['Camila'],
  },
  [PollyLanguage.ROMANIAN]: {
    engine: PollyEngine.STANDARD,
    voices: ['Carmen'],
  },
  [PollyLanguage.RUSSIAN]: {
    engine: PollyEngine.STANDARD,
    voices: ['Tatyana'],
  },
  [PollyLanguage.SPANISH]: { engine: PollyEngine.GENERATIVE, voices: ['Lupe'] },
  [PollyLanguage.SWEDISH]: { engine: PollyEngine.NEURAL, voices: ['Elin'] },
  [PollyLanguage.TURKISH]: { engine: PollyEngine.NEURAL, voices: ['Burcu'] },
  [PollyLanguage.WELSH]: { engine: PollyEngine.NEURAL, voices: ['Gwyneth'] },
};

/**
 * Returns the best engine and first female voice for a language.
 * Engine and voiceId are resolved independently: an event override for only
 * one field still uses the language-appropriate default for the other.
 * English defaults to Ruth on generative when not overridden.
 */
export const selectVoiceForLanguage = (
  language: PollyLanguage,
  voicesByLanguageFromEvent?: Partial<
    Record<PollyLanguage, { engine: string; voiceId: string }>
  >,
): { engine: PollyEngine; voiceId: string } => {
  const fromEvent = voicesByLanguageFromEvent?.[language];
  const entry = voicesByLanguage[language];

  const isEnglish = language === PollyLanguage.ENGLISH;
  const defaultEngine = isEnglish
    ? PollyEngine.GENERATIVE
    : entry?.engine ?? PollyEngine.GENERATIVE;
  const defaultVoiceId = isEnglish ? 'Ruth' : entry?.voices[0] ?? 'Ruth';

  return {
    engine: fromEvent?.engine
      ? (fromEvent.engine as PollyEngine)
      : defaultEngine,
    voiceId: fromEvent?.voiceId ? fromEvent.voiceId : defaultVoiceId,
  };
};
